import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { CreatorBriefDto, CreatorGoal, IdentityDto, IdentitySummaryDto, ProjectIdentityDto } from '@gc/shared';
import { ApiError } from '../../api/client.js';
import { guardarBriefCreador, listarIdentidades, obtenerBriefCreador, obtenerIdentidad, obtenerIdentidadProyecto, vincularIdentidad } from '../../api/identity.js';

export type PanelCreadorProps = {
  readonly projectId: string;
  readonly revision: number;
  readonly status: string;
  onCambio(): void;
};

const OBJETIVOS: Record<CreatorGoal, string> = {
  EDUCATIONAL: 'Educativo',
  LIFESTYLE: 'Estilo de vida',
  PRODUCT: 'Presentación de producto',
};

/**
 * Fase 10 en el proyecto: identidad aprobada vinculada (inmutable para este
 * proyecto) y brief (localización, producto opcional, objetivo). Sin ambos, el
 * storyboard del creador no se genera.
 */
export function PanelCreador({ projectId, revision, status, onCambio }: PanelCreadorProps): React.JSX.Element {
  const [vinculo, setVinculo] = useState<ProjectIdentityDto | null>(null);
  const [identidades, setIdentidades] = useState<IdentitySummaryDto[]>([]);
  const [elegida, setElegida] = useState<IdentityDto | null>(null);
  const [brief, setBrief] = useState<CreatorBriefDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const [goal, setGoal] = useState<CreatorGoal>('LIFESTYLE');
  const [localizacion, setLocalizacion] = useState('');
  const [productoNombre, setProductoNombre] = useState('');
  const [productoDescripcion, setProductoDescripcion] = useState('');
  const [mensajes, setMensajes] = useState('');

  const recargar = useCallback(async () => {
    try {
      const [v, ids, b] = await Promise.all([obtenerIdentidadProyecto(projectId), listarIdentidades(), obtenerBriefCreador(projectId)]);
      setVinculo(v);
      setIdentidades(ids.filter((i) => i.approvedRevision !== null && i.archivedAt === null));
      setBrief(b);
      if (b) {
        setGoal(b.goal); setLocalizacion(b.locationDescription); setProductoNombre(b.productName ?? ''); setProductoDescripcion(b.productDescription ?? ''); setMensajes(b.productMessages.join('\n'));
      }
      setError(null);
    } catch (causa) {
      setError(causa instanceof ApiError ? causa.message : 'No se pudo cargar el creador virtual.');
    }
  }, [projectId]);

  useEffect(() => { void recargar(); }, [recargar, revision]);

  async function accion(fn: () => Promise<unknown>): Promise<void> {
    setOcupado(true); setError(null);
    try { await fn(); await recargar(); onCambio(); }
    catch (causa) { setError(causa instanceof ApiError ? causa.message : 'La operación falló.'); }
    finally { setOcupado(false); }
  }

  const bloqueado = ocupado || status === 'GENERATING' || status === 'RENDERING';
  const revisionAprobada = elegida?.revisions.find((r) => r.status === 'APPROVED') ?? null;

  return (
    <section aria-labelledby="titulo-creador" style={{ border: '1px solid var(--borde)', borderRadius: '6px', padding: '1rem', margin: '1rem 0' }}>
      <h2 id="titulo-creador" style={{ marginTop: 0 }}>Creador virtual</h2>

      <h3>Identidad</h3>
      {vinculo !== null ? (
        <p>
          <strong>{vinculo.identityName}</strong>, revisión {vinculo.revisionNumber} (fija para este proyecto) · {vinculo.references.length} referencia(s)
          <br />
          <span style={{ display: 'inline-flex', gap: '0.5rem', marginTop: '0.25rem' }}>
            {vinculo.references.map((r) => <img key={r.assetId} src={r.contentUrl} alt="" style={{ width: '4rem', height: '4rem', objectFit: 'cover' }} />)}
          </span>
          <br />
          <small>Rasgos: {vinculo.lockedTraits.join('; ')}</small>
        </p>
      ) : (
        <p><small>Sin identidad. Elige una revisión aprobada de tus <Link to="/identidades">identidades</Link>.</small></p>
      )}
      <p>
        <label>
          {vinculo === null ? 'Vincular identidad' : 'Cambiar identidad'}{' '}
          <select
            disabled={bloqueado}
            value={elegida?.id ?? ''}
            onChange={(e) => { const id = e.target.value; if (id === '') setElegida(null); else void obtenerIdentidad(id).then(setElegida).catch(() => setElegida(null)); }}
          >
            <option value="">— elegir —</option>
            {identidades.map((i) => <option key={i.id} value={i.id}>{i.name} (rev. {i.approvedRevision})</option>)}
          </select>
        </label>{' '}
        <button type="button" disabled={bloqueado || revisionAprobada === null} onClick={() => revisionAprobada && void accion(() => vincularIdentidad(projectId, revisionAprobada.id, revision))}>
          Vincular
        </button>
      </p>

      <h3>Brief</h3>
      <p>
        <label>Objetivo{' '}
          <select value={goal} disabled={bloqueado} onChange={(e) => setGoal(e.target.value as CreatorGoal)}>
            {Object.entries(OBJETIVOS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
      </p>
      <p>
        <label htmlFor="brief-localizacion">Localización (en inglés)</label><br />
        <input id="brief-localizacion" value={localizacion} disabled={bloqueado} onChange={(e) => setLocalizacion(e.target.value)} style={{ width: '100%', maxWidth: '40rem' }} placeholder="Bright apartment kitchen with morning light" />
      </p>
      {goal === 'PRODUCT' && (
        <>
          <p>
            <label htmlFor="brief-producto">Producto</label><br />
            <input id="brief-producto" value={productoNombre} disabled={bloqueado} onChange={(e) => setProductoNombre(e.target.value)} style={{ width: '100%', maxWidth: '40rem' }} />
          </p>
          <p>
            <label htmlFor="brief-producto-desc">Descripción fiel del producto</label><br />
            <textarea id="brief-producto-desc" rows={2} value={productoDescripcion} disabled={bloqueado} onChange={(e) => setProductoDescripcion(e.target.value)} style={{ width: '100%', maxWidth: '40rem' }} />
          </p>
          <p>
            <label htmlFor="brief-mensajes">Mensajes autorizados (uno por línea)</label><br />
            <textarea id="brief-mensajes" rows={3} value={mensajes} disabled={bloqueado} onChange={(e) => setMensajes(e.target.value)} style={{ width: '100%', maxWidth: '40rem' }} />
            <br /><small>El sistema no inventa beneficios ni testimonios: solo usa estos mensajes. Los logotipos generados deben revisarse; si la fidelidad importa, usa la foto real del producto en el montaje.</small>
          </p>
        </>
      )}
      <p>
        <button
          type="button"
          disabled={bloqueado || localizacion.trim() === ''}
          onClick={() => void accion(() => guardarBriefCreador(projectId, {
            expectedRevision: revision, goal, locationDescription: localizacion, locationAssetIds: brief?.locationAssetIds ?? [],
            productName: goal === 'PRODUCT' ? productoNombre : null, productDescription: goal === 'PRODUCT' ? productoDescripcion || null : null,
            productAssetIds: brief?.productAssetIds ?? [], productMessages: goal === 'PRODUCT' ? mensajes.split('\n').map((l) => l.trim()).filter(Boolean) : [],
          }))}
        >
          {brief === null ? 'Guardar brief' : 'Actualizar brief'}
        </button>{' '}
        {brief !== null && <small>Guardado el {new Date(brief.updatedAt).toLocaleString('es-ES')}.</small>}
      </p>

      {error !== null && <p role="alert">{error}</p>}
    </section>
  );
}
